import { Loader, Card, Text } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { useEffect, useState } from "react";
import { Trans } from "react-i18next";

import { CheckoutForm } from "./CheckOutForm";
import { useStripePaymentContext } from "./StripePaymentContext";
import { fonts, mapGatewayToStripeKey } from "./constants";
import { useStripeAppearance } from "./useStripeAppearance";

interface Props {
  clientSecret: string | null;
  /**
   * The Stripe gateway that determines which Stripe account to use.
   *
   * This value comes from the backend when creating a PaymentIntent
   * and ensures we use the correct Stripe account (including prepay accounts).
   */
  stripeGateway: string | null;
}

export function StripePayment(props: Props) {
  const { clientSecret, stripeGateway } = props;
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const { state: stripePaymentState } = useStripePaymentContext();
  const appearance = useStripeAppearance();

  // Memoize the Stripe promise to avoid multiple clients on re-renders
  // Memoize it inside the component to prevent CSP errors for routes that don't use Stripe
  // Use the gateway returned from the backend to ensure we use the correct Stripe account
  useEffect(() => {
    if (!stripeGateway) return;
    const stripeKey = mapGatewayToStripeKey[stripeGateway];
    if (!stripeKey) {
      console.error(`No Stripe key configured for gateway: ${stripeGateway}`);
      return;
    }
    setStripePromise(loadStripe(stripeKey));
  }, [stripeGateway]);

  return (
    <Card className={styles.card}>
      {clientSecret && (
        <Elements
          options={{ clientSecret, appearance, fonts }}
          stripe={stripePromise}
        >
          <CheckoutForm />
        </Elements>
      )}
      {stripePaymentState === "fetching-client-secret" && (
        <Loader size="default" />
      )}
      {stripePaymentState === "error" && (
        <Text color="text-danger">
          <Trans>Something went wrong</Trans>
        </Text>
      )}
    </Card>
  );
}

const styles = {
  card: css({
    padding: 16,
  }),
};
